import jsPDF from "jspdf";
import { assetUrl } from "../api/client.js";
import { placeholderImage } from "./placeholders.js";

/**
 * Triggers a brochure download for a design, regardless of whether the admin
 * has uploaded a real PDF.
 *
 *  - We prefer `design.generatedBrochurePdf` — the multi-page brochure the
 *    backend generates during Smart Upload (cover, specs, plan, map,
 *    contact). Looks identical for every design and stays current with the
 *    real spec data.
 *  - We then fall back to `design.brochurePdf` — the optional marketing PDF
 *    the admin uploaded manually.
 *  - When neither exists, we synthesise a single-page client-side PDF from
 *    the spec data using jsPDF so the visitor never gets a dead button.
 *
 * Either way the user always ends up with a real PDF on disk.
 */
export async function downloadDesignBrochure(design) {
  const filename = `${slug(design.name ?? "design")}-brochure.pdf`;

  const sources = [design?.generatedBrochurePdf, design?.brochurePdf].filter(Boolean);
  for (const src of sources) {
    const url = assetUrl(src);
    try {
      const res = await fetch(url, { mode: "cors" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      saveBlob(blob, filename);
      return;
    } catch {
      // try next source / fall through to client-side generation
    }
  }

  const blob = await buildClientBrochure(design);
  saveBlob(blob, filename);
}

/**
 * Build a styled brochure PDF for a design entirely on the client.
 * Single A4 page: hero image up top, name + collection, spec block,
 * description excerpt, contact strip.
 */
async function buildClientBrochure(design) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const W = doc.internal.pageSize.getWidth(); // 210
  const H = doc.internal.pageSize.getHeight(); // 297
  const M = 15; // margin

  // Background — soft cream so text is readable when printed
  doc.setFillColor(250, 248, 240);
  doc.rect(0, 0, W, H, "F");

  // Hero band
  const heroH = 95;
  const heroSrc = pickHeroImage(design);
  try {
    const dataUrl = await urlToDataUrl(heroSrc);
    doc.addImage(dataUrl, "JPEG", 0, 0, W, heroH, undefined, "FAST");
  } catch {
    // Fallback: gold band if the hero image can't be loaded
    doc.setFillColor(212, 175, 55);
    doc.rect(0, 0, W, heroH, "F");
  }

  // Dark gradient strip at the bottom of the hero so the title reads well
  doc.setFillColor(0, 0, 0);
  doc.setGState(new doc.GState({ opacity: 0.55 }));
  doc.rect(0, heroH - 35, W, 35, "F");
  doc.setGState(new doc.GState({ opacity: 1 }));

  // Brand mark
  doc.setTextColor(212, 175, 55);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("SHERE-E-PUNJAB HOMES", M, 14);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text("Bespoke custom home builder", M, 19);

  // Title overlay on hero
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.setFont("helvetica", "bold");
  doc.text(design?.name ?? "Custom design", M, heroH - 14);
  if (design?.collection?.name) {
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(design.collection.name, M, heroH - 7);
  }

  // Spec block
  let y = heroH + 14;
  doc.setTextColor(30, 30, 30);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Specifications", M, y);
  y += 6;

  const specs = [
    ["Storey", design?.storey ?? "—"],
    ["Bedrooms", design?.bedrooms ?? "—"],
    ["Bathrooms", design?.bathrooms ?? "—"],
    ["Living areas", design?.livingAreas ?? 1],
    ["Garage", design?.garage ?? 0],
    [
      "Area",
      design?.areaSqm
        ? `${design.areaSqm} m²${design.areaSq ? ` (${design.areaSq} sq)` : ""}`
        : "—",
    ],
    [
      "Length × Width",
      design?.length && design?.width ? `${design.length}m × ${design.width}m` : "—",
    ],
    ["Block fit", design?.lotFits || "—"],
  ];

  doc.setDrawColor(220, 215, 200);
  doc.setLineWidth(0.2);
  const colW = (W - 2 * M) / 2;
  specs.forEach(([label, value], i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = M + col * colW;
    const rowY = y + row * 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 110, 90);
    doc.text(String(label).toUpperCase(), x, rowY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.text(String(value), x, rowY + 5);
    if (col === 0) {
      doc.line(x + colW - 5, rowY - 2, x + colW - 5, rowY + 9);
    }
  });
  y += Math.ceil(specs.length / 2) * 12 + 6;

  // Description
  if (design?.description) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(30, 30, 30);
    doc.text("About this design", M, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(60, 55, 45);
    const plain = stripHtml(design.description);
    const lines = doc.splitTextToSize(plain, W - 2 * M);
    const room = H - y - 30; // leave space for footer
    const visible = lines.slice(0, Math.max(1, Math.floor(room / 5)));
    doc.text(visible, M, y);
    y += visible.length * 5 + 4;
  }

  // Footer band
  const footerH = 22;
  doc.setFillColor(20, 20, 25);
  doc.rect(0, H - footerH, W, footerH, "F");
  doc.setTextColor(212, 175, 55);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Talk to us", M, H - footerH + 8);
  doc.setTextColor(220, 220, 220);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(
    "sherepunjabhomes.com.au · enquiries@sherepunjabhomes.com.au",
    M,
    H - footerH + 14
  );
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(
    `Generated ${new Date().toLocaleDateString("en-AU")}`,
    W - M,
    H - footerH + 14,
    { align: "right" }
  );

  return doc.output("blob");
}

function pickHeroImage(design) {
  const real = design?.heroImage || design?.images?.[0];
  if (real) return assetUrl(real);
  const key = design?.slug ?? design?._id ?? design?.name ?? "design";
  return placeholderImage("design", key);
}

/**
 * Fetch an image and return a data: URL so jsPDF can embed it.
 * Falls back to a tiny transparent pixel if the fetch fails.
 */
async function urlToDataUrl(url) {
  const res = await fetch(url, { mode: "cors" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a tick so the click is processed
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function slug(s) {
  return String(s)
    .toLowerCase()
    .replace(/[^\w]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function stripHtml(html) {
  return String(html)
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}
